import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { uipApiInstance } from '../../services';
import { mpgetproposaldetail } from '../../services/clients';
import {
    API_VERSION,
    CLIENT_PROFILE_STAGE_KEYS,
    FEATURE_NAMES,
    getEwsDurationKeys,
    PARTICIPANT_ID_AVAILABLE_TENANTS,
    STATUSVARIANTS
} from '../../common/constants';
import { AdviceClientType, AdviceClientType as InitialState } from '../../common/types';
import { logEventToBackEnd, updateGlobalLoaderState } from '../../features/global/globalSlice';
import { splitClientName } from '../../common/helper';
import { removeLoader, showLoader, syncAdviceGoals } from '../../features/client-goals';
import { checkFFEnabled, getClientJourneyFlowType } from '../../utils';
import { startLoading, stopLoading } from '../../features/global/appLoader';
import {
    clearUnsavedPlanChanges,
    shouldKeepPlanEditsInStore,
    trackUnsavedAccountChanges
} from '../../features/unsaved-plan-changes';

const sanitizeAccountSourceUpdateFlags = (sources: any[] = []) =>
    sources.map((source: any) => {
        // Newly created (FE-generated) sources must be sent without an `update` flag.
        // eslint-disable-next-line no-unused-vars
        const { isNew, ...sourceWithoutIsNew } = source || {};
        if (!sourceWithoutIsNew?.accountId || isNew) {
            // eslint-disable-next-line no-unused-vars
            const { update, ...rest } = sourceWithoutIsNew;
            return rest;
        }
        return { ...sourceWithoutIsNew, update: sourceWithoutIsNew.update === true };
    });

const sanitizeProposalPayload = (payload: any) => {
    if (!checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
        return payload;
    }
    const sanitized = { ...payload };
    if (sanitized.profile?.accounts) {
        const accounts = { ...sanitized.profile.accounts };
        if (accounts.incomeSources) {
            // Keep `isNew` here — sanitizeAccountSourceUpdateFlags consumes it to omit the `update`
            // flag for FE-generated new accounts and strips it from the final payload.
            accounts.incomeSources = sanitizeAccountSourceUpdateFlags(
                accounts.incomeSources.map(({ createdAt, updatedAt, ...rest }: any) => ({ ...rest }))
            );
        }
        if (accounts.investmentsSources) {
            accounts.investmentsSources = sanitizeAccountSourceUpdateFlags(
                accounts.investmentsSources.map(({ createdAt, updatedAt, ...rest }: any) => ({ ...rest }))
            );
        }
        sanitized.profile = { ...sanitized.profile, accounts };
    }
    if (sanitized.proposals?.formFields) {
        sanitized.proposals = {
            ...sanitized.proposals,
            formFields: sanitized.proposals.formFields.map((field: any) => {
                if (field.data) {
                    const { createdAt, updatedAt, isNew, ...restData } = field.data;
                    field.data = restData;
                }
                return field;
            })
        };
    }
    if (sanitized.proposals?.global) {
        const { planCreationDate, lastUpdateDate, lastUpdatedDate, ...restGlobal } = sanitized.proposals.global;
        sanitized.proposals = { ...sanitized.proposals, global: restGlobal };
    }
    return sanitized;
};

const initialState: InitialState = {
    _id: '',
    clientDob: '',
    clientFirstName: '',
    clientLastName: '',
    tenant: '',
    relationship: [],
    riskProfile: '',
    accounts: { incomeSources: [], investmentsSources: [] },
    stages: [CLIENT_PROFILE_STAGE_KEYS[0]],
    loading: false,
    error: '',
    step: 0, // tracks the current step of the client creation process. (on the Stepper)
    editMode: false,
    hasUnsavedChanges: false,
    enableConfirmConsoleNavigation: false,
    disableConfirmConsoleNavigation: false,
    retirementAge: '',
    planningAge: '',
    participant_id: '',
    isParticipantIdAvailable: false
};

const adviceClient = createSlice({
    name: 'adviceCreateClient',
    initialState,
    reducers: {
        initializeAdviceClient: (state) => ({ ...initialState, tenant: state.tenant }),
        syncAdviceClient: (state, action: PayloadAction<AdviceClientType>) => {
            const payload = action.payload;

            // Split firstName if it contains spaces and lastName is empty
            if (payload.clientFirstName && payload.clientFirstName.includes(' ') && !payload.clientLastName) {
                const { firstName, lastName } = splitClientName(payload.clientFirstName);
                payload.clientFirstName = firstName;
                payload.clientLastName = lastName;
            }

            const resolvedTenant = payload?.tenant ?? state?.tenant ?? '';
            state = {
                ...payload,
                participant_id: payload?.participant_id ?? state?.participant_id ?? '',
                isParticipantIdAvailable:
                    payload?.isParticipantIdAvailable ?? PARTICIPANT_ID_AVAILABLE_TENANTS.includes(resolvedTenant)
            };
            return state;
        },
        updateClientIncomeSources: (state, action: PayloadAction<any>) => {
            state.accounts.incomeSources = action.payload;
        },
        updateClientInvestmentSources: (state, action: PayloadAction<any>) => {
            state.accounts.investmentsSources = action.payload;
        },
        // We need to keep track of which step we are on in Redux else
        // if we are in step 2 and make any change to the redux store, the whole application
        //re-renders and it moves us to step 1.
        updateCurrentStep: (state, action: PayloadAction<any>) => {
            state.step = action.payload;
        },
        setDisableConfirmConsoleNavigation: (state, action: PayloadAction<any>) => {
            state.disableConfirmConsoleNavigation = action.payload;
        },
        setEnableConfirmConsoleNavigation: (state, action: PayloadAction<any>) => {
            state.enableConfirmConsoleNavigation = action.payload;
        },
        clearClientError: (state) => {
            state.error = '';
        }
    },
    extraReducers: (builder) => {
        builder.addCase(createClient.fulfilled, (state, action) => {
            const response = action.payload;

            // Handle name splitting if firstName contains spaces and lastName is empty
            let clientFirstName = response.clientFirstName;
            let clientLastName = response.clientLastName;

            if (clientFirstName && clientFirstName.includes(' ') && !clientLastName) {
                const { firstName, lastName } = splitClientName(clientFirstName);
                clientFirstName = firstName;
                clientLastName = lastName;
            }

            state._id = response._id;
            state.clientDob = response.clientDob;
            state.clientFirstName = clientFirstName;
            state.clientLastName = clientLastName;
            state.tenant = response.tenant;
            state.stages = response.stages;
            state.accounts = response.accounts;
            state.relationship = response.relationship;
            state.error = '';
            state.loading = false;
            state.riskProfile = response.riskProfile;
            state.step = response.step;
            state.retirementAge = response?.retirementAge;
            state.planningAge = response?.planningAge;
            state.participant_id = response?.participant_id;
            state.isParticipantIdAvailable =
                response?.isParticipantIdAvailable ?? PARTICIPANT_ID_AVAILABLE_TENANTS.includes(response?.tenant ?? '');
        }),
            builder.addCase(updateClient.fulfilled, (state) => {
                state.error = '';
            });
    }
});

export const createClient = createAsyncThunk(
    'advice/createclientprofile',
    async (data: any, { dispatch, rejectWithValue }) => {
        try {
            dispatch(startLoading());
            const response = await uipApiInstance({
                method: 'POST',
                url: '/api/advice/createclientprofile',
                withCredentials: false,
                data
            });
            dispatch(logEventToBackEnd('CREATE_CLIENT_PROFILE_SUCCESS'));
            dispatch(syncAdviceClient(response?.data));
            return response.data;
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('CREATE_CLIENT_PROFILE_ERROR'));
            return rejectWithValue(err);
        } finally {
            dispatch(stopLoading());
        }
    }
);

export const saveClient = (data: any) => {
    return async (dispatch: any, getState: any) => {
        const state = getState();
        const isFlow2 = getClientJourneyFlowType(state?.auth?.user?.tenant) === 'flow2';

        if (!shouldKeepPlanEditsInStore(state)) {
            const keysToRemove = ['hasUnsavedChanges', 'enableConfirmConsoleNavigation', 'error'];
            keysToRemove.forEach((key) => {
                if (key in data) delete data[key];
            });
            return dispatch(updateClient(data)).unwrap();
        } else {
            const accounts = isFlow2 ? dispatch(trackUnsavedAccountChanges(data.accounts)) : data.accounts;
            dispatch(
                syncAdviceClient({ ...data, accounts, hasUnsavedChanges: true, enableConfirmConsoleNavigation: true })
            );
            return { success: true };
        }
    };
};

export const updateClient = createAsyncThunk(
    'advice/updateclientprofile',
    async (data: any, { dispatch, rejectWithValue, getState }: any) => {
        const state = getState();
        const { status } = state.adviceGoals?.global || {};
        try {
            const sanitizedData = checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])
                ? {
                      ...data,
                      accounts: {
                          ...data.accounts,
                          incomeSources: sanitizeAccountSourceUpdateFlags(data.accounts?.incomeSources),
                          investmentsSources: sanitizeAccountSourceUpdateFlags(data.accounts?.investmentsSources)
                      }
                  }
                : data;
            // AEX-3363: echo client_ref_id (flow2 + FF only) so the backend can reject a save made
            // against a client copy that was changed in another tab. flow1 stays untouched.
            const isFlow2 = getClientJourneyFlowType(state?.auth?.user?.tenant) === 'flow2';
            const clientRefId = state?.adviceClient?.client_ref_id;
            const clientPayload =
                checkFFEnabled(FEATURE_NAMES['AEX-3342_ADD_VALIDATION_BEFORE_UPDATE']) && isFlow2 && clientRefId
                    ? { ...sanitizedData, client_ref_id: clientRefId }
                    : sanitizedData;
            dispatch(startLoading());
            const response = await uipApiInstance({
                method: 'POST',
                url: '/api/advice/updateclientprofile',
                withCredentials: false,
                data: clientPayload
            });
            let output = {
                ...response?.data,
                accounts: {
                    incomeSources:
                        response.data.accounts?.incomeSources?.map((source: any) => {
                            let oldstatedata = sanitizedData.accounts?.incomeSources?.find(
                                (s: any) => s?.accountId === source.accountId
                            );
                            if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
                                if (!oldstatedata) {
                                    // newly created source — backend just assigned accountId; no update flag
                                    // eslint-disable-next-line no-unused-vars
                                    const { update, ...rest } = source;
                                    return rest;
                                }
                                return { ...source, update: false };
                            }
                            const { update, ...rest } = source;
                            return rest;
                        }) || [],
                    investmentsSources:
                        response.data.accounts?.investmentsSources?.map((source: any) => {
                            let oldstatedata = sanitizedData.accounts?.investmentsSources?.find(
                                (s: any) => s?.accountId === source.accountId
                            );
                            if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
                                if (!oldstatedata) {
                                    // eslint-disable-next-line no-unused-vars
                                    const { update, ...rest } = source;
                                    return rest;
                                }
                                return { ...source, update: false };
                            }
                            const { update, ...rest } = source;
                            return rest;
                        }) || []
                },
                // Keep the client-side Stepper step (the step the UI just advanced to) instead
                // of whatever the backend returns, so `syncAdviceClient` doesn't reset the step
                // and bounce the user back to the previous step. Falls back to the current step
                // in the store when the request payload omits it.
                step: data?.step ?? state.adviceClient?.step
            };

            dispatch(logEventToBackEnd('UPDATE_CLIENT_PROFILE_SUCCESS'));
            dispatch(syncAdviceClient(output));
            if (status === 'complete') {
                dispatch(
                    syncAdviceGoals({
                        ...state.adviceGoals,
                        global: { ...state.adviceGoals.global, status: 'incomplete' }
                    })
                );
            }
            return output;
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('UPDATE_CLIENT_PROFILE_ERROR'));
            return rejectWithValue(err);
        } finally {
            dispatch(stopLoading());
        }
    }
);

// AEX-3363: "Refresh Plan" recovery for the stale-plan banner. Re-fetches the freshest plan from
// the backend and replaces the stale copy in the store so the advisor can re-apply their change on
// top of the latest data. Mirrors the console's open-plan mapping (loadClientToStoreFromProposal).
// A full page reload is not an option here: the store is persisted to sessionStorage, so a reload
// would just rehydrate the same stale data.
export const refreshStalePlan = createAsyncThunk(
    'advice/refreshStalePlan',
    async (_arg: void, { dispatch, getState, rejectWithValue }: any) => {
        const state = getState();
        const proposalId = state?.adviceGoals?._id;
        const tenant = state?.adviceClient?.tenant || state?.adviceGoals?.tenant;
        if (!proposalId || !tenant) return rejectWithValue('MISSING_PLAN_CONTEXT');
        try {
            dispatch(updateGlobalLoaderState(true));
            const proposalData = await mpgetproposaldetail({ proposalId, tenant });
            const rebalancer = (proposalData?.rebalancer_ref_id as any) || {};
            dispatch(
                syncAdviceGoals({
                    _id: proposalData._id || '',
                    apiName: proposalData.api_name || '',
                    tenant: proposalData.tenant,
                    apiVer: proposalData.api_ver || API_VERSION,
                    assignedClientId: proposalData.assignedClientId,
                    formFields: proposalData.form_fields || [],
                    request: proposalData.request || [],
                    response: proposalData.response || [],
                    isMultiGoal: proposalData.is_multi_goal || false,
                    iwsApplied: proposalData.iws_applied || false,
                    loading: false,
                    error: '',
                    goeConfigEmail: proposalData.goeConfigEmail,
                    da_advice_id: proposalData?.da_advice_id,
                    // AEX-3363: keep the freshest proposal reference id so the next save echoes it.
                    proposal_ref_id: proposalData.proposal_ref_id,
                    global: {
                        ...proposalData.global,
                        status: STATUSVARIANTS.INCOMPLETE,
                        affectedEwsTerms: getEwsDurationKeys(Boolean(state?.global?.globalConfig?.isMultiPortfolioTenant)),
                        planExecutionResponse: {
                            rebalancerResult: rebalancer?.response,
                            rebalancer_ref_id: rebalancer?._id
                        },
                        planExecutionStatus:
                            rebalancer?.response && Object.keys(rebalancer.response).length > 0 ? 'success' : 'idle'
                    },
                    infusionindex: {}
                })
            );
            // The refreshed plan replaces any edits kept in the store.
            dispatch(clearUnsavedPlanChanges());
            // The plan document carries the latest client profile + accounts under `client_profile`
            // (same shape save_proposal returns, incl. client_ref_id). Merge it over the current
            // client so a profile/account change made in the other tab is reflected too, without
            // wiping fields it omits.
            const freshProfile = proposalData?.client_profile;
            if (freshProfile) {
                dispatch(
                    syncAdviceClient({
                        ...state.adviceClient,
                        ...freshProfile,
                        step: state.adviceClient?.step,
                        loading: false,
                        hasUnsavedChanges: false,
                        enableConfirmConsoleNavigation: false
                    })
                );
            }
            return proposalData;
        } catch (err) {
            dispatch(logEventToBackEnd('REFRESH_STALE_PLAN_ERROR'));
            return rejectWithValue(err);
        } finally {
            dispatch(updateGlobalLoaderState(false));
        }
    }
);

export const saveProposal = createAsyncThunk(
    'advice/saveProposal',
    async (data: any, { dispatch, rejectWithValue, getState }: any) => {
        try {
            dispatch(showLoader());
            const state = getState();
            const isPlanRun = state?.adviceGoals?.global?.isPlanRun;
            let payload = data;
            if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
                const reduxFormFields: any[] = state?.adviceGoals?.formFields || [];
                const reduxIncomeSources: any[] = state?.adviceClient?.accounts?.incomeSources || [];
                const reduxInvestmentsSources: any[] = state?.adviceClient?.accounts?.investmentsSources || [];

                const resolveAccountUpdateFlag = (source: any, reduxSources: any[]) => {
                    // Newly created (FE-generated) sources must not carry an `update` flag.
                    if (source?.isNew) return source;
                    if (!isPlanRun) return { ...source, update: false };
                    if (!source?.accountId) return source;
                    const reduxMatch = reduxSources.find((s: any) => s?.accountId === source.accountId);
                    const resolvedUpdate = reduxMatch?.update === true || source.update === true;
                    return { ...source, update: resolvedUpdate };
                };

                payload = {
                    ...data,
                    profile: {
                        ...data.profile,
                        accounts: {
                            ...(data.profile?.accounts || {}),
                            incomeSources: (data.profile?.accounts?.incomeSources || []).map((source: any) =>
                                resolveAccountUpdateFlag(source, reduxIncomeSources)
                            ),
                            investmentsSources: (data.profile?.accounts?.investmentsSources || []).map((source: any) =>
                                resolveAccountUpdateFlag(source, reduxInvestmentsSources)
                            )
                        }
                    },
                    proposals: {
                        ...data.proposals,
                        formFields: (data.proposals?.formFields || []).map((field: any) => {
                            // Newly created (FE-generated) goals must not carry an `update` flag.
                            if (!field.data?.goalId || field.data?.isNew) {
                                const { update, ...restData } = field.data || {};
                                return { ...field, data: restData };
                            }
                            const reduxField = reduxFormFields.find((rf: any) => rf.goalId === field.goalId);
                            const updateFlag = reduxField?.data?.update;
                            const resolvedUpdate =
                                updateFlag !== undefined
                                    ? updateFlag
                                    : field.data?.update !== undefined
                                    ? field.data.update
                                    : false;
                            return { ...field, data: { ...field.data, update: resolvedUpdate } };
                        })
                    }
                };
            }
            let finalData = sanitizeProposalPayload(payload);
            if (
                checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021']) &&
                !isPlanRun &&
                finalData?.profile?.accounts
            ) {
                // After sanitizeProposalPayload, existing sources carry an `update` key while newly
                // created (FE-generated) sources do not; only force `update: false` on the former.
                const setUpdateFalse = (sources: any[] = []) =>
                    sources.map((s: any) => ('update' in s ? { ...s, update: false } : s));
                finalData = {
                    ...finalData,
                    profile: {
                        ...finalData.profile,
                        accounts: {
                            ...finalData.profile.accounts,
                            incomeSources: setUpdateFalse(finalData.profile.accounts.incomeSources),
                            investmentsSources: setUpdateFalse(finalData.profile.accounts.investmentsSources)
                        }
                    }
                };
            }
            const response = await uipApiInstance({
                method: 'POST',
                url: '/api/advice/save_proposal',
                withCredentials: false,
                data: finalData
            });

            dispatch(logEventToBackEnd('SAVE_PROPOSAL_SUCCESS'));
            const { profile, proposals } = response.data;

            if (profile) {
                dispatch(syncAdviceClient(profile));
            }

            if (proposals?.document) {
                const doc = proposals.document;
                const fields = doc.form_fields || doc.formFields || [];

                let recommendationArr: any[] = [];
                fields.forEach((goal: any) => {
                    recommendationArr.push({
                        goalId: goal.goalId,
                        oneTimeTopUp: 0,
                        topUpAccumulation: 0,
                        topUpDecumulation: 0,
                        recomendedTenure: 0
                    });
                });

                const transformedProposals = {
                    ...doc,
                    formFields: fields,
                    request: doc.request || [],
                    response: doc.response || [],
                    recommendation: recommendationArr,
                    loading: false,
                    error: ''
                };

                dispatch(syncAdviceGoals(transformedProposals));
            }

            return response.data;
        } catch (err) {
            console.error('Save Proposal Error', err);
            dispatch(logEventToBackEnd('SAVE_PROPOSAL_ERROR'));
            return rejectWithValue(err);
        } finally {
            dispatch(removeLoader());
            dispatch(setEnableConfirmConsoleNavigation(false));
        }
    }
);
export const {
    initializeAdviceClient,
    syncAdviceClient,
    updateClientIncomeSources,
    updateClientInvestmentSources,
    updateCurrentStep,
    // updateClientLoading,
    setDisableConfirmConsoleNavigation,
    setEnableConfirmConsoleNavigation,
    clearClientError
} = adviceClient.actions;

export default adviceClient.reducer;
